import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ordersTabsMessages as messages } from './useOrdersTabs.messages';

import type { TabItem, TabItemId, TabItems } from '@garnish/layout';

// ─────────────────────────────────────────────────────────────────────────────

export const useOrdersTabs = (params: UseOrdersTabsParams) => {
  const { tabIdParam } = params;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const firstTab = useMemo<TabItem>(
    () => ({
      id: 'digital-orders',
      panelId: 'orders.digital-orders-tab-panel',
      label: formatMessage(messages.digitalOrdersTabLabel),
      'aria-label': formatMessage(messages.digitalOrdersTabA11yLabel),
    }),
    [formatMessage],
  );

  const secondTab = useMemo<TabItem>(
    () => ({
      id: 'in-store-orders',
      panelId: 'orders.in-store-orders-tab-panel',
      label: formatMessage(messages.inStoreOrdersTabLabel),
      'aria-label': formatMessage(messages.inStoreOrdersTabA11yLabel),
    }),
    [formatMessage],
  );

  const tabs = useMemo<TabItems>(() => [firstTab, secondTab], [firstTab, secondTab]);

  const validTabIdParam = new Set([firstTab.id, secondTab.id]);

  const [activeTabItemId, setActiveTabItemId] = useState<TabItemId>(
    tabIdParam && validTabIdParam.has(tabIdParam) ? tabIdParam : firstTab.id,
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    activeTabItemId,
    setActiveTabItemId,
    tabs,
    firstTab,
    secondTab,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOrdersTabsParams = {
  tabIdParam?: OrderTabId;
};

type OrderTabId = 'digital-orders' | 'in-store-orders';
